import axios from 'axios';
import { SERVER_NAME } from '../config';

/**
 * Checks if an image is appropriate using AI content moderation
 * @param {File} imageFile - The image file to check
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<{isAppropriate: boolean, reason: string|null}>} - Result object
 */
export const checkImageAppropriate = async (imageFile, onProgress = null) => {
  try {
    if (onProgress) onProgress({ status: 'checking', message: 'Analyzing image content...' });
    
    // Convert the file to base64 for sending to the API
    const base64Image = await fileToBase64(imageFile);
    
    // Create form data for the server request
    const formData = new FormData();
    formData.append('image', base64Image);
    formData.append('filename', imageFile.name);
    
    // Send to our backend which will handle the OpenAI API call
    const response = await axios.post(`${SERVER_NAME}/moderateContent`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    if (onProgress) onProgress({ status: 'complete', message: 'Analysis complete' });
    
    if (response.data.success) {
      return {
        isAppropriate: response.data.isAppropriate,
        reason: response.data.reason || null,
        moderationData: response.data.moderationData || null
      };
    } else {
      console.error('Content moderation failed:', response.data.error);
      // In case of API failure, we should err on the side of caution
      return { 
        isAppropriate: false, 
        reason: 'Content moderation service unavailable. Please try again later.' 
      };
    }
  } catch (error) {
    console.error('Error checking image appropriateness:', error);
    return { 
      isAppropriate: false, 
      reason: 'Error analyzing image. Please try again or use a different image.' 
    };
  }
};

/**
 * Checks if text content is appropriate
 * @param {string} text - The text to check
 * @returns {Promise<{isAppropriate: boolean, reason: string|null}>} - Result object
 */
export const checkTextAppropriate = async (text) => {
  try {
    const response = await axios.post(`${SERVER_NAME}/moderateText`, { text });
    
    if (response.data.success) {
      return {
        isAppropriate: response.data.isAppropriate,
        reason: response.data.reason || null,
        moderationData: response.data.moderationData || null
      };
    } else {
      console.error('Text moderation failed:', response.data.error);
      return { isAppropriate: true, reason: null }; // Default to allowing if service fails
    }
  } catch (error) {
    console.error('Error checking text appropriateness:', error);
    return { isAppropriate: true, reason: null }; // Default to allowing if service fails
  }
};

/**
 * Helper function to convert a file to base64
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });
};
